import { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import { useToast } from "@/components/base/Toast";
import { usePipelineActivities } from "@/components/feature/PipelineActivityContext";
import { Breadcrumb } from "./components/Breadcrumb";
import { LeadHeaderCard } from "./components/LeadHeaderCard";
import { ContactInfoCard } from "./components/ContactInfoCard";
import { ActivityTimeline } from "./components/ActivityTimeline";
import { LeadScoreCard } from "./components/LeadScoreCard";
import { PipelineStageCard } from "./components/PipelineStageCard";
import { QuickActionsCard } from "./components/QuickActionsCard";
import { AssignedTeamCard } from "./components/AssignedTeamCard";
import { CallDialerPopup } from "./components/CallDialerPopup";
import { PostCallDrawer } from "./components/PostCallDrawer";
import { MoveToStageModal } from "./components/MoveToStageModal";
import { AddActivityModal } from "./components/AddActivityModal";
import AddLeadModal from "@/pages/leads/components/AddLeadModal";
import { LeadQuotationsCard } from "@/pages/quotations/components/LeadQuotationsCard";
import WhatsAppChatCard from "./components/WhatsAppChatCard";
import { allStages } from "@/mocks/leadsData";
import type { Lead, LeadStage } from "@/mocks/leadsData";
import type { Activity } from "@/mocks/leadActivityData";
import { defaultSellingTypes, getSellingTypeColorClass, getSellingTypeBgClass } from "@/mocks/sellingTypesData";
import { fetchApi } from "@/services/apiService";

export default function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { getActivitiesForLead: getPipelineActivitiesForLead } = usePipelineActivities();
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);

  // State for modals & components
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [showDialer, setShowDialer] = useState(false);
  const [showPostCallDrawer, setShowPostCallDrawer] = useState(false);
  const [callData, setCallData] = useState<{ duration: number; status: "connected" | "missed" | "busy" } | null>(null);
  const [showMoveToStage, setShowMoveToStage] = useState(false);
  const [moveToTargetStage, setMoveToTargetStage] = useState<LeadStage>("New Inquiry");
  const [leadStage, setLeadStage] = useState<LeadStage>("New Inquiry");

  // Dynamic projects and selling types
  const [projectsList, setProjectsList] = useState<any[]>([]);
  const [sellingTypesList, setSellingTypesList] = useState<any[]>([]);
  const [dbStages, setDbStages] = useState<string[]>([]);
  const [stageDefinitions, setStageDefinitions] = useState<any[]>([]);

  // Link project state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkProjectId, setLinkProjectId] = useState<string | null>(null);
  const [linkItemId, setLinkItemId] = useState<string | null>(null);
  const [linkProjectSearch, setLinkProjectSearch] = useState("");
  const [linkedProject, setLinkedProject] = useState<{ projectId: string; itemId?: string; itemLabel?: string } | null>(null);

  // Track extra activities added manually
  const [extraActivities, setExtraActivities] = useState<Activity[]>([]);

  const stagesList = dbStages.length > 0 ? dbStages : allStages;

  // Fetch dynamic pipeline stages, projects, and selling types
  useEffect(() => {
    fetchApi<any[]>('/config/pipeline-stages/get-all', undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        if (items.length > 0) {
          setStageDefinitions(items);
          setDbStages(items.map((s: any) => s.name).filter(Boolean));
        }
      })
      .catch(() => {});

    fetchApi<any[]>('/projects', undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        setProjectsList(items);
      })
      .catch(() => {});

    fetchApi<any[]>('/projects/settings/selling-types', undefined, defaultSellingTypes)
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        if (items.length > 0) setSellingTypesList(items);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchLeadDetail = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const res = await fetchApi<any>(`/leads/${id}`, undefined, null);
        if (isMounted && res) {
          setLead(res);
          setLeadStage(res.stage || "New Inquiry");
          if (res.projectId || res.project_id) {
            setLinkedProject({ projectId: res.projectId || res.project_id });
          }
          if (res.activities) {
            setExtraActivities(
              res.activities.map((a: any) => ({
                id: a.id,
                leadId: Number(id),
                type: (a.type || "Call") as Activity["type"],
                description: a.description || "",
                agent: a.performed_by || "System",
                timestamp: a.created_at ? new Date(a.created_at).toLocaleString() : "Just now",
                systemEvent: false,
              }))
            );
          }
        }
      } catch (err) {
        console.error("Error loading lead detail:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchLeadDetail();
    return () => {
      isMounted = false;
    };
  }, [id]);

  const filteredProjects = linkProjectSearch.trim()
    ? projectsList.filter((p) => p.name?.toLowerCase().includes(linkProjectSearch.toLowerCase()))
    : projectsList;
  const selectedProject = linkProjectId ? projectsList.find((p) => p.id === linkProjectId) : null;
  const projectItems: any[] = [];
  const projectSellingType = selectedProject?.sellingTypeId
    ? sellingTypesList.find((st) => st.id === selectedProject.sellingTypeId)
    : null;
  const pstLabel = projectSellingType?.itemLabel || "Item";
  const linkedProjectData = linkedProject
    ? projectsList.find((p) => p.id === linkedProject.projectId)
    : null;
  const linkedProjectST = linkedProjectData?.sellingTypeId
    ? sellingTypesList.find((st) => st.id === linkedProjectData.sellingTypeId)
    : null;

  const activities = useMemo(() => {
    if (!lead) return [];
    const pipelineActivities = getPipelineActivitiesForLead(lead.id).map((a) => ({
      ...a,
      type: "Stage Change" as Activity["type"],
    }));
    return [...extraActivities, ...pipelineActivities];
  }, [lead, getPipelineActivitiesForLead, extraActivities]);

  const leadInitials = lead
    ? lead.name.split(" ").map((n) => n[0]).join("").toUpperCase()
    : "";

  // Get next stage
  const getNextStage = (current: string): string | null => {
    const idx = stagesList.indexOf(current);
    if (idx < 0 || idx >= stagesList.length - 1) return null;
    return stagesList[idx + 1];
  };

  // Call handlers
  const handleOpenDialer = () => {
    setShowDialer(true);
  };

  const handleEndCall = (duration: number, status: "connected" | "missed" | "busy") => {
    setShowDialer(false);
    setCallData({
      duration,
      status,
    });
    setTimeout(() => {
      setShowPostCallDrawer(true);
    }, 200);
  };

  const getCallTime = (): string => {
    const now = new Date();
    return `Today ${now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`;
  };

  const handleSaveCallLog = async (data: { notes: string; newStage: LeadStage; extraFieldValues: Record<string, string> }) => {
    if (!callData || !lead) return;

    setLeadStage(data.newStage);
    const m = Math.floor(callData.duration / 60);
    const s = callData.duration % 60;
    const durStr = m > 0 ? `${m}m ${s}s` : `${s}s`;

    let desc = `Call logged — ${durStr} • ${callData.status.charAt(0).toUpperCase() + callData.status.slice(1)}`;
    if (data.notes) desc += ` — "${data.notes.slice(0, 60)}${data.notes.length > 60 ? "..." : ""}"`;

    const extraFieldPairs = Object.entries(data.extraFieldValues).filter(([, v]) => typeof v === 'string' ? v.trim() : (v !== undefined && v !== null && v !== ''));
    if (extraFieldPairs.length > 0) {
      const fields = extraFieldPairs.map(([k, v]) => `${k}: ${v}`).join(", ");
      desc += `\n${fields}`;
    }

    if (data.newStage !== lead.stage) {
      desc += `\nStage: ${lead.stage} → ${data.newStage}`;
    }

    const mergedExtraFields = {
      ...(lead.extraFields || (lead as any).extra_fields || {}),
      ...data.extraFieldValues,
    };

    try {
      await fetchApi(`/leads/${lead.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          stage: data.newStage,
          extraFields: mergedExtraFields,
        }),
      });
      await fetchApi(`/leads/${lead.id}/activities`, {
        method: 'POST',
        body: JSON.stringify({ type: 'Call', description: desc, performedBy: lead.assigned || 'System' }),
      });
      setLead((prev) => (prev ? { ...prev, stage: data.newStage, extraFields: mergedExtraFields, extra_fields: mergedExtraFields } : prev));
      showToast("Call logged successfully", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to log call", "error");
    }

    setExtraActivities((prev) => [
      {
        id: Date.now(),
        leadId: lead.id,
        type: "Call",
        description: desc,
        agent: lead.assigned || "System",
        timestamp: "Just now",
        systemEvent: false,
      },
      ...prev,
    ]);
  };

  // Edit lead handler
  const handleEditLead = () => {
    setShowEditModal(true);
  };

  const handleSaveLead = async (data: Omit<Lead, "id" | "lastActivity">) => {
    if (!lead) return;
    try {
      const updated = await fetchApi<Lead>(`/leads/${lead.id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      setLead(updated || { ...lead, ...data });
      setLeadStage(data.stage);
      showToast("Lead updated successfully", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to update lead", "error");
    }
    setShowEditModal(false);
  };

  // Add activity handler
  const handleAddActivityClick = () => {
    setShowAddActivity(true);
  };

  const handleSaveActivity = async (data: { type: string; description: string; date: string; time: string; agent: string }) => {
    if (!lead) return;
    const timestamp = `${data.date} at ${data.time}`;
    try {
      await fetchApi(`/leads/${lead.id}/activities`, {
        method: 'POST',
        body: JSON.stringify({ type: data.type, description: data.description, performedBy: data.agent }),
      });
      showToast("Activity added", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to add activity", "error");
    }

    setExtraActivities((prev) => [
      {
        id: Date.now(),
        leadId: lead.id,
        type: data.type as Activity["type"],
        description: data.description,
        agent: data.agent,
        timestamp,
        systemEvent: false,
      },
      ...prev,
    ]);
  };

  // Move to stage handlers
  const handleMoveToNextStage = () => {
    if (!lead) return;
    const next = getNextStage(leadStage);
    if (!next) return;
    setMoveToTargetStage(next as LeadStage);
    setShowMoveToStage(true);
  };

  const handleSelectStage = (stageName: string) => {
    if (!lead || stageName === leadStage) return;
    setMoveToTargetStage(stageName as LeadStage);
    setShowMoveToStage(true);
  };

  const handleConfirmMove = async (extraFieldValues: Record<string, string>) => {
    if (!lead) return;
    const prevStage = leadStage;
    setLeadStage(moveToTargetStage);

    let desc = `Stage changed from ${prevStage} to ${moveToTargetStage}`;
    const extraPairs = Object.entries(extraFieldValues).filter(([, v]) => typeof v === 'string' ? v.trim() : (v !== undefined && v !== null && v !== ''));
    if (extraPairs.length > 0) {
      desc += `\n${extraPairs.map(([k, v]) => `${k}: ${v}`).join(", ")}`;
    }

    const mergedExtraFields = {
      ...(lead.extraFields || (lead as any).extra_fields || {}),
      ...extraFieldValues,
    };

    try {
      await fetchApi(`/leads/${lead.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          stage: moveToTargetStage,
          extraFields: mergedExtraFields,
        }),
      });
      await fetchApi(`/leads/${lead.id}/activities`, {
        method: 'POST',
        body: JSON.stringify({ type: 'Stage Change', description: desc, performedBy: lead.assigned || 'System' }),
      });
      setLead((prev) => (prev ? { ...prev, stage: moveToTargetStage, extraFields: mergedExtraFields, extra_fields: mergedExtraFields } : prev));
      showToast(`Lead moved to ${moveToTargetStage}`, "success");
    } catch (err: any) {
      showToast(err.message || "Failed to update stage", "error");
    }

    setExtraActivities((prev) => [
      {
        id: Date.now(),
        leadId: lead.id,
        type: "Stage Change",
        description: desc,
        agent: lead.assigned || "System",
        timestamp: "Just now",
        systemEvent: false,
      },
      ...prev,
    ]);
  };

  if (loading) {
    return (
      <CrmLayout title="Loading Lead...">
        <div className="p-12 flex flex-col items-center justify-center min-h-[50vh]">
          <i className="ri-loader-4-line text-3xl text-indigo-600 animate-spin mb-3" />
          <p className="text-sm text-gray-500 font-medium">Loading lead details...</p>
        </div>
      </CrmLayout>
    );
  }

  if (!lead) {
    return (
      <CrmLayout title="Lead Not Found">
        <div className="p-5 md:p-6 flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <i className="ri-user-unfollow-line text-2xl text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Lead not found</h2>
          <p className="text-sm text-gray-500 mb-4">This lead may have been deleted or the link is invalid.</p>
          <button
            onClick={() => navigate("/leads")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line" /> Back to Leads
          </button>
        </div>
      </CrmLayout>
    );
  }

  return (
    <CrmLayout title={`${lead.name} — Lead Detail`}>
      <div className="p-5 md:p-6 space-y-5">
        <Breadcrumb leadName={lead.name} />

        <LeadHeaderCard
          lead={{ ...lead, stage: leadStage }}
          onEdit={handleEditLead}
          onAddActivity={handleAddActivityClick}
        />

        <div className="flex flex-col lg:flex-row gap-5">
          {/* Left column — 65% */}
          <div className="w-full lg:w-[65%] space-y-5">
            <ContactInfoCard lead={lead} />
            <ActivityTimeline activities={activities} onLogActivity={handleAddActivityClick} />
            <LeadQuotationsCard leadId={lead.id} />
            <WhatsAppChatCard
              leadId={lead.id}
              leadName={lead.name}
              leadPhone={lead.phone}
              leadStage={lead.stage}
              leadValue={lead.value}
            />
          </div>

          {/* Right column — 35% */}
          <div className="w-full lg:w-[35%] space-y-5">
            <LeadScoreCard lead={lead} />
            <PipelineStageCard
              currentStage={leadStage}
              stages={stagesList}
              onMoveToNextStage={handleMoveToNextStage}
              onSelectStage={handleSelectStage}
            />
            <QuickActionsCard
              onLogCall={handleOpenDialer}
              onSendWhatsApp={() => navigate(`/whatsapp?leadId=${lead.id}`)}
            />
            <AssignedTeamCard lead={lead} onReassign={handleEditLead} />

            {/* Linked Project Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Linked Project</h3>
              {linkedProject && linkedProjectData ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full flex-shrink-0 ${linkedProjectST ? getSellingTypeColorClass(linkedProjectST.color) : "bg-indigo-500"}`} />
                    <p className="text-sm font-medium text-gray-900">{linkedProjectData.name}</p>
                  </div>
                  {linkedProjectST && (
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${getSellingTypeBgClass(linkedProjectST.color)}`}>
                      {linkedProjectST.name}
                    </span>
                  )}
                  {linkedProject.itemId && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">{linkedProject.itemLabel || "Item"}</p>
                      <p className="text-xs font-medium text-gray-900">{linkedProject.itemId}</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => navigate(`/projects/${linkedProject.projectId}`)}
                      className="text-xs text-indigo-600 hover:text-indigo-700 font-medium cursor-pointer"
                    >
                      View Project
                    </button>
                    <button
                      onClick={async () => {
                        setLinkedProject(null);
                        try {
                          await fetchApi(`/leads/${lead.id}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ projectId: null }),
                          });
                          showToast("Project unlinked", "info");
                        } catch {}
                      }}
                      className="text-xs text-red-500 hover:text-red-600 font-medium cursor-pointer"
                    >
                      Unlink
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                    <i className="ri-building-line text-gray-300 text-lg w-5 h-5 flex items-center justify-center" />
                  </div>
                  <p className="text-sm text-gray-500">Not linked to any project</p>
                  <button
                    onClick={() => { setShowLinkModal(true); setLinkProjectId(null); setLinkItemId(null); setLinkProjectSearch(""); }}
                    className="w-full mt-3 px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-600 rounded-lg hover:bg-indigo-50 whitespace-nowrap cursor-pointer transition-colors"
                  >
                    + Link to Project
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Lead Modal */}
      {showEditModal && (
        <AddLeadModal
          lead={{ ...lead, stage: leadStage }}
          onClose={() => setShowEditModal(false)}
          onSave={handleSaveLead}
        />
      )}

      {/* Add Activity Modal */}
      {showAddActivity && (
        <AddActivityModal
          onClose={() => setShowAddActivity(false)}
          onSave={handleSaveActivity}
        />
      )}

      {/* Call Dialer Popup */}
      {showDialer && (
        <CallDialerPopup
          leadName={lead.name}
          leadInitials={leadInitials}
          leadPhone={lead.phone}
          onClose={() => setShowDialer(false)}
          onEndCall={handleEndCall}
        />
      )}

      {/* Post Call Drawer */}
      {showPostCallDrawer && callData && (
        <PostCallDrawer
          leadName={lead.name}
          leadPhone={lead.phone}
          currentStage={leadStage}
          stageDefinitions={stageDefinitions}
          stages={stagesList}
          callData={{ ...callData, callTime: getCallTime() }}
          onClose={() => setShowPostCallDrawer(false)}
          onSave={handleSaveCallLog}
        />
      )}

      {/* Move to Stage Modal */}
      {showMoveToStage && (
        <MoveToStageModal
          leadName={lead.name}
          targetStage={moveToTargetStage}
          stageDefinitions={stageDefinitions}
          onClose={() => setShowMoveToStage(false)}
          onConfirm={handleConfirmMove}
        />
      )}

      {/* Link to Project Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[85vh] overflow-y-auto mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">Link Lead to Project</h3>
              <button
                onClick={() => setShowLinkModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Select Project</label>
                <input
                  type="text"
                  value={linkProjectSearch}
                  onChange={(e) => setLinkProjectSearch(e.target.value)}
                  placeholder="Search project..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 mb-2"
                />
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {filteredProjects.map((p) => {
                    const st = sellingTypesList.find((s) => s.id === p.sellingTypeId);
                    const isSelected = linkProjectId === p.id;
                    return (
                      <button
                        key={p.id}
                        onClick={() => {
                          setLinkProjectId(isSelected ? null : p.id);
                          setLinkItemId(null);
                        }}
                        className={`w-full flex items-center justify-between p-2.5 rounded-lg border transition-all cursor-pointer text-left ${
                          isSelected
                            ? "border-indigo-600 bg-indigo-50/60 text-indigo-900"
                            : "border-gray-100 hover:border-gray-200 text-gray-700"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full ${st ? getSellingTypeColorClass(st.color) : "bg-indigo-500"}`} />
                          <span className="text-sm font-medium">{p.name}</span>
                        </div>
                        {st && (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${getSellingTypeBgClass(st.color)}`}>
                            {st.name}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
              <button
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 whitespace-nowrap cursor-pointer transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!linkProjectId || !lead) return;
                  const selectedProj = projectsList.find((p) => p.id === linkProjectId);
                  setLinkedProject({
                    projectId: linkProjectId,
                  });
                  setShowLinkModal(false);
                  try {
                    await fetchApi(`/leads/${lead.id}`, {
                      method: 'PATCH',
                      body: JSON.stringify({ projectId: linkProjectId }),
                    });
                    await fetchApi(`/leads/${lead.id}/activities`, {
                      method: 'POST',
                      body: JSON.stringify({
                        type: 'project_link',
                        description: `Linked to project: ${selectedProj?.name || linkProjectId}`,
                        performedBy: lead.assigned || 'System',
                      }),
                    });
                    setExtraActivities((prev) => [
                      {
                        id: Date.now(),
                        leadId: lead.id,
                        type: "project_link" as any,
                        description: `Linked to project: ${selectedProj?.name || linkProjectId}`,
                        agent: lead.assigned || "System",
                        timestamp: "Just now",
                        systemEvent: false,
                      },
                      ...prev,
                    ]);
                    showToast(`Linked to ${selectedProj?.name || 'project'} successfully`, "success");
                  } catch {
                    showToast("Linked to project", "info");
                  }
                }}
                disabled={!linkProjectId}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg whitespace-nowrap cursor-pointer transition-colors ${
                  linkProjectId ? "bg-indigo-600 hover:bg-indigo-700" : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                Link to Project
              </button>
            </div>
          </div>
        </div>
      )}
    </CrmLayout>
  );
}